import { useId, useState, type ReactNode } from 'react';
import CountedField from './CountedField';
import { SERVICE_TEXT_LIMITS, type FieldErrors } from './servicePageDraft';

/**
 * Spec 081a — one value row of an option plus its description expander. The row's own controls
 * are passed as children (unchanged markup); the expander toggle sits at the end of the row and
 * opens an EN/AR pair below it, bound to the value's existing description/descriptionAr.
 */
export interface ValueDescriptionEditorProps {
  valueLabel: string;
  description: string;
  descriptionAr: string;
  onChange: (patch: { description?: string; descriptionAr?: string }) => void;
  /** e.g. `variations[1].values[0]` */
  errorPrefix: string;
  errors: FieldErrors;
  children: ReactNode;
}

export default function ValueDescriptionEditor({
  valueLabel, description, descriptionAr, onChange, errorPrefix, errors, children,
}: ValueDescriptionEditorProps) {
  const id = useId();
  const [open, setOpen] = useState(() => !!(description || descriptionAr));
  const enError = errors[`${errorPrefix}.description`];
  const arError = errors[`${errorPrefix}.descriptionAr`];
  const show = open || !!enError || !!arError;
  const name = valueLabel.trim() || 'this value';
  const hasText = !!(description || descriptionAr);

  return (
    <div className="svc-value-block">
      <div className="v2-value-row">
        {children}
        <button type="button" className={`svc-expander-btn svc-expander-btn--row${hasText ? ' svc-expander-btn--filled' : ''}`}
          aria-expanded={show} aria-controls={`${id}-panel`} aria-label={`Description for ${name}`}
          title={show ? 'Hide description' : 'Add a description'} onClick={() => setOpen((o) => !o)}>
          <span aria-hidden="true">{show ? '▾' : '▸'}</span> Description
        </button>
      </div>
      {show && (
        <div id={`${id}-panel`} className="svc-page__pair svc-value-block__panel">
          <CountedField label={`Description of ${name} (English)`} lang="en" multiline rows={2} value={description}
            maxLength={SERVICE_TEXT_LIMITS.valueDescription} error={enError}
            onChange={(v) => onChange({ description: v })} testId="value-description" />
          <CountedField label={`Description of ${name} (Arabic)`} lang="ar" multiline rows={2} value={descriptionAr}
            maxLength={SERVICE_TEXT_LIMITS.valueDescription} error={arError}
            onChange={(v) => onChange({ descriptionAr: v })} testId="value-description-ar" />
        </div>
      )}
    </div>
  );
}
